// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { costAdjustmentSchema } from '@/asset/api/types';
import {
  CostAdjustmentReason,
  CostAdjustmentType,
} from '@/constants/cost-adjustment';
import { Currency } from '@/currency/api/types';
import { serializeCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const createCostAdjustmentResponseSchema = z
  .object({
    data: costAdjustmentSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, adjustment: data }));

export interface CreateCostAdjustmentParameters {
  adjustmentType: CostAdjustmentType;
  effectiveDate: DateTime;
  amount: Currency;
  reason: CostAdjustmentReason;
  notes: string | null;
}

export async function createCostAdjustment(
  assetKey: string,
  parameters: CreateCostAdjustmentParameters
) {
  const path = `/v1/asset/${assetKey}/cost-adjustment`;
  const response = await api.post(path, {
    adjustmentType: parameters.adjustmentType,
    effectiveDate: serializeDateForAPI(parameters.effectiveDate),
    amount: serializeCurrencyForAPI(parameters.amount),
    reason: parameters.reason,
    notes: parameters.notes,
  });

  return parseResponse(createCostAdjustmentResponseSchema, response.data, {
    schemaName: 'CreateAssetCostAdjustment',
    endpoint: path,
  });
}
